import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Box, Flex, Card, Grid, Heading, Text, TextField, Switch, Select, IconButton, Button, Badge, Separator } from "@radix-ui/themes"
import {
  Settings,
  CreditCard,
  Save,
  CheckCircle,
  XCircle,
  DollarSign,
  Shield,
  Info,
  X,
  Percent
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useToast } from "../../hooks/use-toast"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

interface PaymentMethod {
  _id: string
  name: string
  code: string
  description: string
  icon: string
  isEnabled: boolean
  isDefault: boolean
  customProcessingFee: number | null
  customProcessingFeeType: 'percentage' | 'fixed'
  minAmount: number | null
  maxAmount: number | null
  processingFee: number
  processingFeeType: 'percentage' | 'fixed'
  supportedCurrencies: string[]
  configuration: any
  sortOrder: number
}

const RestaurantSettingsPage: React.FC = () => {
  const { restaurantId } = useParams()
  const { toast } = useToast()
  const { t, language, dir } = useLanguage()
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // General settings state
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(false)
  const [orderNotifications, setOrderNotifications] = useState(true)
  const [lowStockAlerts, setLowStockAlerts] = useState(true)
  const [customerReviews, setCustomerReviews] = useState(true)
  const [analyticsTracking, setAnalyticsTracking] = useState(true)
  const [autoArchiveOrders, setAutoArchiveOrders] = useState(false)

  // Tax settings state
  const [taxEnabled, setTaxEnabled] = useState(false)
  const [taxPercentage, setTaxPercentage] = useState(15)

  // Service fees settings state
  const [serviceFeeEnabled, setServiceFeeEnabled] = useState(false)
  const [serviceFeePercentage, setServiceFeePercentage] = useState(5)

  useEffect(() => {
    fetchPaymentMethods()
    fetchGeneralSettings()
  }, [restaurantId])

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true)
      const response = await restaurantPortalAPI.getRestaurantPaymentMethods(restaurantId!)
      setPaymentMethods(response.data.data)
    } catch (error) {
      console.error('Error fetching payment methods:', error)
      toast({
        title: t("error"),
        description: t("failedToLoadPaymentMethods"),
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchGeneralSettings = async () => {
    try {
      // In a real implementation, you would fetch these settings from the API
      // For now, we'll use default values
      setAutoAcceptOrders(false)
      setOrderNotifications(true)
      setLowStockAlerts(true)
      setCustomerReviews(true)
      setAnalyticsTracking(true)
      setAutoArchiveOrders(false)

      // Tax settings
      setTaxEnabled(false)
      setTaxPercentage(15)

      // Service fees settings
      setServiceFeeEnabled(false)
      setServiceFeePercentage(5)
    } catch (error) {
      console.error('Error fetching general settings:', error)
    }
  }

  const handleTogglePaymentMethod = async (methodId: string, isEnabled: boolean) => {
    try {
      if (isEnabled) {
        await restaurantPortalAPI.enableRestaurantPaymentMethod(restaurantId!, methodId)
      } else {
        await restaurantPortalAPI.disableRestaurantPaymentMethod(restaurantId!, methodId)
      }

      toast({
        title: t("success"),
        description: isEnabled ? t("paymentMethodEnabled") : t("paymentMethodDisabled"),
      })

      fetchPaymentMethods()
    } catch (error) {
      console.error('Error toggling payment method:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdatePaymentMethod"),
        variant: "destructive",
      })
    }
  }

  const handleSetDefault = async (methodId: string) => {
    try {
      await restaurantPortalAPI.setDefaultPaymentMethod(restaurantId!, methodId)
      toast({
        title: t("success"),
        description: t("defaultPaymentMethodSet"),
      })
      fetchPaymentMethods()
    } catch (error) {
      console.error('Error setting default payment method:', error)
      toast({
        title: t("error"),
        description: t("failedToSetDefaultPaymentMethod"),
        variant: "destructive",
      })
    }
  }

  const handleUpdateCustomFee = async (methodId: string, fee: number | null, feeType: 'percentage' | 'fixed') => {
    try {
      await restaurantPortalAPI.updatePaymentMethodCustomFee(restaurantId!, methodId, {
        customProcessingFee: fee,
        customProcessingFeeType: feeType
      })
      toast({
        title: t("success"),
        description: t("customFeeUpdated"),
      })
      fetchPaymentMethods()
    } catch (error) {
      console.error('Error updating custom fee:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateCustomFee"),
        variant: "destructive",
      })
    }
  }

  // General settings handlers
  const handleToggleAutoAcceptOrders = async (enabled: boolean) => {
    try {
      setAutoAcceptOrders(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("autoAcceptOrdersEnabled") : t("autoAcceptOrdersDisabled"),
      })
    } catch (error) {
      console.error('Error updating auto-accept orders setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateSetting"),
        variant: "destructive",
      })
    }
  }

  const handleToggleOrderNotifications = async (enabled: boolean) => {
    try {
      setOrderNotifications(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("orderNotificationsEnabled") : t("orderNotificationsDisabled"),
      })
    } catch (error) {
      console.error('Error updating order notifications setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateSetting"),
        variant: "destructive",
      })
    }
  }

  const handleToggleLowStockAlerts = async (enabled: boolean) => {
    try {
      setLowStockAlerts(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("lowStockAlertsEnabled") : t("lowStockAlertsDisabled"),
      })
    } catch (error) {
      console.error('Error updating low stock alerts setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateSetting"),
        variant: "destructive",
      })
    }
  }

  const handleToggleCustomerReviews = async (enabled: boolean) => {
    try {
      setCustomerReviews(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("customerReviewsEnabled") : t("customerReviewsDisabled"),
      })
    } catch (error) {
      console.error('Error updating customer reviews setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateSetting"),
        variant: "destructive",
      })
    }
  }

  const handleToggleAnalyticsTracking = async (enabled: boolean) => {
    try {
      setAnalyticsTracking(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("analyticsTrackingEnabled") : t("analyticsTrackingDisabled"),
      })
    } catch (error) {
      console.error('Error updating analytics tracking setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateSetting"),
        variant: "destructive",
      })
    }
  }

  const handleToggleAutoArchiveOrders = async (enabled: boolean) => {
    try {
      setAutoArchiveOrders(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("autoArchiveOrdersEnabled") : t("autoArchiveOrdersDisabled"),
      })
    } catch (error) {
      console.error('Error updating auto-archive orders setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateSetting"),
        variant: "destructive",
      })
    }
  }

  // Tax settings handlers
  const handleToggleTax = async (enabled: boolean) => {
    try {
      setTaxEnabled(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("taxEnabled") : t("taxDisabled"),
      })
    } catch (error) {
      console.error('Error updating tax setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateTax"),
        variant: "destructive",
      })
    }
  }

  const handleUpdateTaxPercentage = async (percentage: number) => {
    try {
      setTaxPercentage(percentage)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: t("taxPercentageUpdated", { percentage: percentage.toString() }),
      })
    } catch (error) {
      console.error('Error updating tax percentage:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateTaxPercentage"),
        variant: "destructive",
      })
    }
  }

  // Service fees settings handlers
  const handleToggleServiceFee = async (enabled: boolean) => {
    try {
      setServiceFeeEnabled(enabled)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: enabled ? t("serviceFeeEnabled") : t("serviceFeeDisabled"),
      })
    } catch (error) {
      console.error('Error updating service fee setting:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateServiceFee"),
        variant: "destructive",
      })
    }
  }

  const handleUpdateServiceFeePercentage = async (percentage: number) => {
    try {
      setServiceFeePercentage(percentage)
      // In a real implementation, you would save this to the API
      toast({
        title: t("success"),
        description: t("serviceFeePercentageUpdated", { percentage: percentage.toString() }),
      })
    } catch (error) {
      console.error('Error updating service fee percentage:', error)
      toast({
        title: t("error"),
        description: t("failedToUpdateServiceFeePercentage"),
        variant: "destructive",
      })
    }
  }

  const handleSaveAllSettings = async () => {
    try {
      setSaving(true)

      const enabledMethods = paymentMethods
        .filter(pm => pm.isEnabled)
        .map((pm, index) => ({
          paymentMethodId: pm._id,
          isEnabled: pm.isEnabled,
          isDefault: pm.isDefault,
          customProcessingFee: pm.customProcessingFee,
          customProcessingFeeType: pm.customProcessingFeeType,
          minAmount: pm.minAmount,
          maxAmount: pm.maxAmount,
          configuration: pm.configuration
        }))

      await restaurantPortalAPI.updateRestaurantPaymentMethods(restaurantId!, {
        paymentMethods: enabledMethods
      })

      // In a real implementation, you would also save the general settings
      const generalSettings = {
        autoAcceptOrders,
        orderNotifications,
        lowStockAlerts,
        customerReviews,
        analyticsTracking,
        autoArchiveOrders,
        taxEnabled,
        taxPercentage,
        serviceFeeEnabled,
        serviceFeePercentage
      }

      // Save general settings to API
      // await restaurantPortalAPI.updateGeneralSettings(restaurantId!, generalSettings)

      toast({
        title: t("success"),
        description: t("settingsSavedSuccessfully"),
      })
    } catch (error) {
      console.error('Error saving settings:', error)
      toast({
        title: t("error"),
        description: t("failedToSaveSettings"),
        variant: "destructive",
      })
    } finally {
      setSaving(false)
    }
  }

  const getStatusBadgeVariant = (isEnabled: boolean): "solid" | "soft" => {
    return isEnabled ? "solid" : "soft"
  }

  const getDefaultBadgeVariant = (isDefault: boolean): "solid" | "soft" => {
    return isDefault ? "solid" : "soft"
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "SDG",
    }).format(amount)
  }

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingSettings")} />
  }

  return (
    <Flex direction="column" gap="6" dir={dir}>
      <PageHeader
        title={t("restaurantSettings")}
        subtitle={t("configureRestaurantPreferences")}
      >
        <Button
          onClick={handleSaveAllSettings}
          disabled={saving}
          variant="solid"
          color="red"
          size="3"
        >
          <Save className={`h-4 w-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
          {saving ? t("saving") : t("saveSettings")}
        </Button>
      </PageHeader>

      {/* Tax Settings Section */}
      <DataCard
        title={t("taxSettings")}
        subtitle={t("configureTaxSettingsForRestaurant")}
      >
        <Text size="2" color="gray" mb="4">
          {t("configureTaxSettingsDescription")}
        </Text>

        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          <Card variant="surface">
            <Flex direction="column" gap="3" p="4">
              <Flex
                justify="between"
                align="center"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("enableTax")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("applyTaxToAllOrders")}
                  </Text>
                </Box>
                <Switch
                  checked={taxEnabled}
                  onCheckedChange={handleToggleTax}
                />
              </Flex>
            </Flex>
          </Card>

          <Card variant="surface">
            <Flex direction="column" gap="2" p="4">
              <Text size="2" weight="medium" mb="1">
                {t("taxPercentage")}
              </Text>
              <Text size="1" color="gray" mb="2">
                {t("setTaxRatePercentage")}
              </Text>
              <Flex align="center" gap="2">
                <TextField.Root
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={taxPercentage.toString()}
                  onChange={(e) => handleUpdateTaxPercentage(parseFloat(e.target.value) || 0)}
                  placeholder="15"
                  disabled={!taxEnabled}
                  style={{ flex: 1 }}
                />
                <Text size="2" color="gray">%</Text>
              </Flex>
            </Flex>
          </Card>
        </Grid>

        {taxEnabled && (
          <Box mt="4" p="4" style={{ backgroundColor: 'var(--blue-3)', borderRadius: 'var(--radius-3)' }}>
            <Flex align="start" gap="3">
              <Percent className="h-4 w-4" style={{ color: 'var(--blue-11)', marginTop: '2px' }} />
              <Box>
                <Text size="2" weight="medium" mb="1">
                  {t("taxConfigurationActive")}
                </Text>
                <Text size="1" color="gray">
                  {t("taxWillBeAppliedAt", { percentage: taxPercentage })}
                </Text>
              </Box>
            </Flex>
          </Box>
        )}
      </DataCard>

      {/* Service Fees Settings Section */}
      <DataCard
        title={t("serviceFeeSettings")}
        subtitle={t("configureServiceFeeSettingsForRestaurant")}
      >
        <Text size="2" color="gray" mb="4">
          {t("configureServiceFeeSettingsDescription")}
        </Text>

        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          <Card variant="surface">
            <Flex direction="column" gap="3" p="4">
              <Flex
                justify="between"
                align="center"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("enableServiceFee")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("applyServiceFeeToAllOrders")}
                  </Text>
                </Box>
                <Switch
                  checked={serviceFeeEnabled}
                  onCheckedChange={handleToggleServiceFee}
                />
              </Flex>
            </Flex>
          </Card>

          <Card variant="surface">
            <Flex direction="column" gap="2" p="4">
              <Text size="2" weight="medium" mb="1">
                {t("serviceFeePercentage")}
              </Text>
              <Text size="1" color="gray" mb="2">
                {t("setServiceFeeRatePercentage")}
              </Text>
              <Flex align="center" gap="2">
                <TextField.Root
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={serviceFeePercentage.toString()}
                  onChange={(e) => handleUpdateServiceFeePercentage(parseFloat(e.target.value) || 0)}
                  placeholder="5"
                  disabled={!serviceFeeEnabled}
                  style={{ flex: 1 }}
                />
                <Text size="2" color="gray">%</Text>
              </Flex>
            </Flex>
          </Card>
        </Grid>

        {serviceFeeEnabled && (
          <Box mt="4" p="4" style={{ backgroundColor: 'var(--green-3)', borderRadius: 'var(--radius-3)' }}>
            <Flex align="start" gap="3">
              <Percent className="h-4 w-4" style={{ color: 'var(--green-11)', marginTop: '2px' }} />
              <Box>
                <Text size="2" weight="medium" mb="1">
                  {t("serviceFeeConfigurationActive")}
                </Text>
                <Text size="1" color="gray">
                  {t("serviceFeeWillBeAppliedAt", { percentage: serviceFeePercentage })}
                </Text>
              </Box>
            </Flex>
          </Box>
        )}
      </DataCard>

      {/* Payment Methods Section */}
      <DataCard
        title={t("paymentMethods")}
        subtitle={t("configurePaymentMethodsForCustomers")}
      >
        <Text size="2" color="gray" mb="4">
          {t("configurePaymentMethodsDescription")}
        </Text>

        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          {paymentMethods.map((method) => (
            <Card key={method._id} variant="surface">
              <Flex direction="column" gap="4" p="4">
                <Flex
                  justify="between"
                  align="center"
                  direction={language === 'ar' ? 'row-reverse' : 'row'}
                  gap="3"
                >
                  <Flex align="center" gap="3" style={{ flex: 1 }}>
                    <Box
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--gray-3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CreditCard className="h-5 w-5" style={{ color: 'var(--gray-11)' }} />
                    </Box>
                    <Box>
                      <Text size="3" weight="medium">
                        {method.name}
                      </Text>
                      <Text size="1" color="gray">
                        {method.code}
                      </Text>
                    </Box>
                  </Flex>
                  <Switch
                    checked={method.isEnabled}
                    onCheckedChange={(checked) => handleTogglePaymentMethod(method._id, checked)}
                  />
                </Flex>

                <Flex direction="column" gap="3">
                  <Flex justify="between" align="center">
                    <Badge color={method.isEnabled ? "green" : "red"} variant={getStatusBadgeVariant(method.isEnabled)}>
                      {method.isEnabled ? t("active") : t("inactive")}
                    </Badge>
                    <Badge color="gray" variant={getDefaultBadgeVariant(method.isDefault)}>
                      {method.isDefault ? t("default") : t("optional")}
                    </Badge>
                  </Flex>

                  <Text size="2" color="gray">
                    {method.description}
                  </Text>

                  <Flex direction="column" gap="2">
                    <Flex justify="between" align="center">
                      <Text size="1" color="gray">
                        {t("baseProcessingFee")}:
                      </Text>
                      <Text size="2" weight="medium">
                        {method.processingFee} {method.processingFeeType === 'percentage' ? '%' : 'SDG'}
                      </Text>
                    </Flex>

                    {method.isEnabled && (
                      <Flex direction="column" gap="2">
                        <Flex justify="between" align="center">
                          <Text size="1" color="gray">
                            {t("customFee")}:
                          </Text>
                          <Flex gap="2" align="center">
                            <TextField.Root
                              type="number"
                              step="0.01"
                              value={method.customProcessingFee?.toString() || ''}
                              onChange={(e) => {
                                const value = e.target.value ? parseFloat(e.target.value) : null
                                handleUpdateCustomFee(method._id, value, method.customProcessingFeeType)
                              }}
                              placeholder="0"
                              size="1"
                              style={{ width: '80px' }}
                            />
                            <Select.Root
                              value={method.customProcessingFeeType}
                              onValueChange={(value) => handleUpdateCustomFee(method._id, method.customProcessingFee, value as 'percentage' | 'fixed')}
                            >
                              <Select.Trigger style={{ width: '80px' }} />
                              <Select.Content>
                                <Select.Item value="fixed">SDG</Select.Item>
                                <Select.Item value="percentage">%</Select.Item>
                              </Select.Content>
                            </Select.Root>
                          </Flex>
                        </Flex>

                        <Flex justify="between" align="center">
                          <Text size="1" color="gray">
                            {t("minAmount")}:
                          </Text>
                          <Text size="2" weight="medium">
                            {method.minAmount ? formatCurrency(method.minAmount) : t("noLimit")}
                          </Text>
                        </Flex>

                        {method.maxAmount && (
                          <Flex justify="between" align="center">
                            <Text size="1" color="gray">
                              {t("maxAmount")}:
                            </Text>
                            <Text size="2" weight="medium">
                              {formatCurrency(method.maxAmount)}
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                    )}

                    {method.isEnabled && !method.isDefault && (
                      <Button
                        variant="outline"
                        size="2"
                        onClick={() => handleSetDefault(method._id)}
                        style={{ width: '100%' }}
                      >
                        <CheckCircle className="h-4 w-4" style={{ marginRight: language === 'ar' ? '0' : '8px', marginLeft: language === 'ar' ? '8px' : '0' }} />
                        {t("setAsDefault")}
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          ))}
        </Grid>

        {paymentMethods.length === 0 && (
          <Flex direction="column" align="center" justify="center" py="8">
            <CreditCard className="h-12 w-12" style={{ color: 'var(--gray-9)', marginBottom: '16px' }} />
            <Heading size="4" mb="2">
              {t("noPaymentMethodsAvailable")}
            </Heading>
            <Text size="2" color="gray">
              {t("contactAdministratorForPaymentMethods")}
            </Text>
          </Flex>
        )}
      </DataCard>

      {/* General Settings Section */}
      <DataCard
        title={t("generalSettings")}
        subtitle={t("configureGeneralRestaurantSettings")}
      >
        <Text size="2" color="gray" mb="4">
          {t("configureGeneralRestaurantSettingsDescription")}
        </Text>

        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          <Flex direction="column" gap="3">
            <Card variant="surface">
              <Flex
                justify="between"
                align="center"
                p="4"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("autoAcceptOrders")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("automaticallyAcceptIncomingOrders")}
                  </Text>
                </Box>
                <Switch
                  checked={autoAcceptOrders}
                  onCheckedChange={handleToggleAutoAcceptOrders}
                />
              </Flex>
            </Card>

            <Card variant="surface">
              <Flex
                justify="between"
                align="center"
                p="4"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("orderNotifications")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("receiveNotificationsForNewOrders")}
                  </Text>
                </Box>
                <Switch
                  checked={orderNotifications}
                  onCheckedChange={handleToggleOrderNotifications}
                />
              </Flex>
            </Card>

            <Card variant="surface">
              <Flex
                justify="between"
                align="center"
                p="4"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("lowStockAlerts")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("getNotifiedWhenItemsLowInStock")}
                  </Text>
                </Box>
                <Switch
                  checked={lowStockAlerts}
                  onCheckedChange={handleToggleLowStockAlerts}
                />
              </Flex>
            </Card>
          </Flex>

          <Flex direction="column" gap="3">
            <Card variant="surface">
              <Flex
                justify="between"
                align="center"
                p="4"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("customerReviews")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("allowCustomersToLeaveReviews")}
                  </Text>
                </Box>
                <Switch
                  checked={customerReviews}
                  onCheckedChange={handleToggleCustomerReviews}
                />
              </Flex>
            </Card>

            <Card variant="surface">
              <Flex
                justify="between"
                align="center"
                p="4"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("analyticsTracking")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("trackCustomerBehaviorAndAnalytics")}
                  </Text>
                </Box>
                <Switch
                  checked={analyticsTracking}
                  onCheckedChange={handleToggleAnalyticsTracking}
                />
              </Flex>
            </Card>

            <Card variant="surface">
              <Flex
                justify="between"
                align="center"
                p="4"
                direction={language === 'ar' ? 'row-reverse' : 'row'}
                gap="3"
              >
                <Box style={{ flex: 1 }}>
                  <Text size="2" weight="medium" mb="1">
                    {t("autoArchiveOrders")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("automaticallyArchiveCompletedOrders")}
                  </Text>
                </Box>
                <Switch
                  checked={autoArchiveOrders}
                  onCheckedChange={handleToggleAutoArchiveOrders}
                />
              </Flex>
            </Card>
          </Flex>
        </Grid>
      </DataCard>

      {/* Information Section */}
      <DataCard
        title={t("information")}
        subtitle={t("importantInformationAboutPaymentMethods")}
      >
        <div className="space-y-4 text-sm">
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Shield className="h-4 w-4 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{t("paymentSecurity")}</p>
              <p className="text-gray-600 dark:text-gray-400">
                {t("allPaymentMethodsSecured")}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <DollarSign className="h-4 w-4 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{t("feeStructure")}</p>
              <p className="text-gray-600 dark:text-gray-400">
                {t("customProcessingFeesDescription")}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <CheckCircle className="h-4 w-4 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{t("defaultPaymentMethod")}</p>
              <p className="text-gray-600 dark:text-gray-400">
                {t("defaultPaymentMethodDescription")}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <Percent className="h-4 w-4 text-orange-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{t("taxConfiguration")}</p>
              <p className="text-gray-600 dark:text-gray-400">
                {t("taxConfigurationDescription")}
              </p>
            </div>
          </div>
        </div>
      </DataCard>
    </Flex>
  )
}

export default RestaurantSettingsPage 